<template>
  <!-- Даты слева, количество и поля справа, снизу - сохранение или дальнейшее действие -->
  <div class="entry-screen is-growing">
    <div class="entry-screen__main">
      <div class="entry-screen__side">
        <dates-calendar
          ref="calendar"
          v-model="dates"
          class="entry-screen__calendar"
          :is-disabled="isDateLocked"
          :marked="dateProblems"
        ></dates-calendar>
      </div>
      <!-- Количество и поля режима; у расхода - с доступным остатком -->
      <div class="entry-screen__table-box">
        <entry-table
          ref="table"
          v-loading="optionsLoading > 0"
          class="entry-screen__table"
          :dates.sync="dates"
          :amounts.sync="amounts"
          :unit="unitName"
          :available="available"
          :fields="modeFields"
          :values.sync="modeValues"
          :show-errors="showErrors"
          empty-text="Выберите даты в календаре слева"
          @passport="openPassport"
        ></entry-table>
      </div>
    </div>
    <div v-if="isIncoming" class="entry-screen__actions">
      <choice-cards
        compact
        :value="action"
        :groups="actionGroups"
        :loading="busy"
        @input="pickAction"
      ></choice-cards>
    </div>
    <!-- Расход: дальнейших действий нет - «Отмена» и «Сохранить» под всеми блоками справа -->
    <div v-else class="entry-screen__actions is-plain">
      <choice-cards
        compact
        :groups="saveGroups"
        :loading="busy"
        @input="onSaveCard($event, saveEntry)"
      ></choice-cards>
    </div>
    <passport-dialog ref="passport" :type="mode"></passport-dialog>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import ChoiceCards from "@/components/ecologist/dataEntryNew/choiceCards";
import DatesCalendar from "@/components/ecologist/dataEntryNew/datesCalendar";
import EntryTable from "@/components/ecologist/dataEntryNew/entryTable";
import PassportDialog from "@/components/ecologist/dataEntryNew/passportDialog";
import StepMixin, { CANCEL, CANCEL_CARD } from "./stepMixin";
import { MODES, OUTGOING, isAvailable } from "./entryModes";

// Карточка «Только сохранить» среди дальнейших действий
const SAVE_ONLY = "saveOnly";
// Пауза после выбора дат перед их проверкой на сервере - пока протягивают интервал, не проверяем
const CHECK_DELAY = 300;

// Этап дат и количества: у расхода - вместе с полями режима и сохранением
export default {
  name: "entryStep",
  components: { ChoiceCards, DatesCalendar, EntryTable, PassportDialog },
  mixins: [StepMixin],

  computed: {
    ...mapState("wasteEntry", ["recalcNoticed"]),
    ...mapGetters("wasteEntry", ["available", "dateProblems", "recalculates"]),
    // Сохранение с отменой - справа, как «Сохранить» у расхода
    actionGroups() {
      return [
        {
          title: "Перейти к следующему движению с отходом",
          items: OUTGOING.filter((type) => isAvailable(type, this.page)).map(
            (type) => ({
              type,
              title: MODES[type].actionTitle || MODES[type].title,
              icon: MODES[type].icon,
            })
          ),
        },
        {
          title: "Сохранить внесенное движение",
          vertical: true,
          items: [
            {
              type: SAVE_ONLY,
              title: "Сохранить",
              icon: "el-icon-check",
              primary: true,
            },
            CANCEL_CARD,
          ],
        },
      ];
    },
  },

  watch: {
    // Даты проверяются сразу при выборе: проблемные краснеют в календаре, у расхода появляется остаток
    dates() {
      clearTimeout(this.checkTimer);
      this.checkTimer = setTimeout(this.checkInBackground, CHECK_DELAY);
    },
  },

  // Даты уже выбраны (вернулись на этап) - watch не сработает, проверяем сразу
  mounted() {
    if (this.dates.length) this.checkInBackground();
  },

  methods: {
    isDateLocked(date) {
      return this.$store.getters.GETISDATEENTRYLOCKED(date);
    },

    // Поступление: «Только сохранить» сохраняет сразу, дальнейшее действие ведёт к его полям
    pickAction(type) {
      if (type === CANCEL) return this.cancel();
      return this.withBusy(type, async () => {
        if (!(await this.validateEntry())) return;
        if (type === SAVE_ONLY) {
          this.chooseAction(null);
          await this.save();
          return;
        }
        this.chooseAction(type);
        this.step = "details";
      });
    },

    // Справочники действия грузятся, пока его поля уже на экране
    chooseAction(type) {
      this.$store
        .dispatch("wasteEntry/chooseAction", type)
        .catch(() => this.$message.error("Не удалось загрузить справочники"));
    },

    // Расход сохраняется прямо с экрана дат
    saveEntry() {
      return this.withBusy("save", async () => {
        if (await this.validateEntry()) await this.save();
      });
    },

    checkDates() {
      return this.$store.dispatch("wasteEntry/checkDates");
    },

    async checkInBackground() {
      try {
        await this.checkDates();
      } catch (error) {
        // Не страшно: даты проверятся ещё раз при сохранении
        return;
      }
      this.noticeRecalculation();
    },

    noticeRecalculation() {
      if (this.recalcNoticed || !this.recalculates) return;
      this.$store.commit("wasteEntry/setRecalcNoticed");
      this.$notify({
        title: "Внимание",
        message:
          "Внесение информации за прошедшие даты приведет к перерасчету количества отходов и изменениям в отчетной документации. Будьте внимательны при заполнении!",
        type: "warning",
        duration: 10000,
      });
    },

    async validateDates() {
      if (!this.dates.length) return this.warn("Выберите хотя бы одну дату");
      try {
        await this.checkDates();
      } catch (error) {
        this.$message.error("Не удалось проверить даты, попробуйте ещё раз");
        return false;
      }
      const problems = Object.keys(this.dateProblems).sort();
      if (problems.length) {
        this.$refs.calendar.showDate(problems[0]);
        this.$message.error(
          "За даты, отмеченные красным, вносить данные нельзя (причина - при наведении). Снимите их выбор."
        );
        return false;
      }
      this.noticeRecalculation();
      return true;
    },

    validateAmounts() {
      const first = this.dates.find((date) => this.amounts[date] !== undefined);
      if (!first) return this.warnInTable("Введите количество");
      // Серые предложенные значения запоминаем как внесённые
      const amounts = {};
      this.dates.forEach(
        (date) =>
          (amounts[date] =
            this.amounts[date] !== undefined
              ? this.amounts[date]
              : this.amounts[first])
      );
      this.amounts = amounts;

      if (!this.isIncoming) {
        const over = this.dates.some(
          (date) => parseFloat(amounts[date]) > this.available[date] + 0.001
        );
        if (over)
          return this.warnInTable(
            "Ошибка. Количество отхода превышает допустимый остаток"
          );
      }
      if (!this.rowsValid(this.modeFields, this.modeValues, false)) {
        this.setShowErrors(true);
        return this.warnInTable("Заполните выделенные поля");
      }
      return true;
    },

    async validateEntry() {
      this.setShowErrors(false);
      return (await this.validateDates()) && this.validateAmounts();
    },
  },

  created() {
    // Без реактивности: таймер проверки дат
    this.checkTimer = null;
  },

  beforeDestroy() {
    clearTimeout(this.checkTimer);
  },
};
</script>

<style scoped src="./entryScreen.css"></style>

<style scoped>
/*
  Экран дат не ниже календаря: если календарь не помещается по высоте,
  экран вытягивается и прокручивается вся область этапа
*/
.entry-screen.is-growing {
  height: auto;
  min-height: 100%;
}
.is-growing .entry-screen__main {
  min-height: auto;
}
/*
  Таблица растянута по обёртке и не влияет на высоту экрана:
  иначе её прокрутка пропала бы, а экран рос вместе со строками
*/
.entry-screen__table-box {
  position: relative;
  flex: 1;
  min-width: 0;
}
.entry-screen__table-box > .entry-screen__table {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}
/* Левая колонка с календарём */
.entry-screen__side {
  flex: 0 0 38%;
  min-width: 340px;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
/* Рамка по высоте самого календаря, а не во всю высоту таблицы */
.entry-screen__calendar {
  flex-shrink: 0;
  padding: 12px;
}
</style>
